/* eslint-disable indent */
import WarningIcon from '@quintoandar/cozy-icons/Warning';
import React, { Fragment, useEffect, useState } from 'react';
import { push } from 'react-router-redux';
import { withRouter } from 'react-router';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { LoadingPage } from 'components/LoadingPage';
import PropTypes from 'prop-types';
import AppBar from 'components/AppBar';
import ROUTES from 'routes/constants';
import { createStructuredSelector } from 'reselect';
import PageWrapper from 'components/PageWrapper';
import DynamicForm from 'components/DynamicForm';
import IconDescription from 'components/IconDescription';
import Button from '@quintoandar/cozy-core/Button';
import Box from '@quintoandar/cozy-core/Box';
import { getUser } from 'utils/user';
import debounce from 'lodash/debounce';
import { FormattedMessage, injectIntl } from 'react-intl';
import { selectAuthorization } from 'containers/Authorization/selectors';
import { checkIsBetaTester } from 'utils/auth';
import messages from './messages';
import { selectChecklist } from '../selectors';

import {
  checklistItemChanged,
  updateStorageChecklistItem,
  fetchChecklistAction,
  fetchChecklistFromStorageAction,
} from '../actions';
import { DEBOUNCE_STORAGE_UPDATE_WAIT_TIME, SECTIONS_TYPE } from '../constants';
import { isChecklistStorageReady } from '../utils/checklistStorage';
import ConditionDescription from './ConditionDescription';
import PropertyDescriptionInput from './PropertyDescriptionInput';

export const GeneralConditions = ({
  goToChecklist,
  fetchChecklistFromStorage,
  fetchChecklist,
  onChangeItemChecklist,
  checklist,
  onChangeItemPropertyDescription,
  intl,
  authorization,
}) => {
  const [shouldValidate, setShouldValidate] = useState(false);
  const [isValid, setIsValid] = useState(true);

  const userId = getUser().user_id;

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (!checklist.hasFetchedChecklistItems) {
      fetchChecklist();
    }

    fetchChecklistFromStorage({ userId });
  }, []);

  const onValidate = isDynamicFormValid => {
    setIsValid(isDynamicFormValid);
    setShouldValidate(false);

    if (isDynamicFormValid) {
      goToChecklist(checkIsBetaTester(authorization));
    }
  };

  const validateForm = () => setShouldValidate(true);

  const isFinished =
    checklist.hasFetchedChecklistItems &&
    isChecklistStorageReady(checklist);

  const ContentComponent = () => (
    <Fragment>
      <PageWrapper>
        <DynamicForm
          onChange={onChangeItemChecklist({
            sectionType: SECTIONS_TYPE.GENERAL_CONDITIONS,
            userId,
          })}
          sections={checklist.generalConditions}
          onValidate={onValidate}
          shouldValidate={shouldValidate}
        >
          {checklist.generalConditions &&
            checklist.generalConditions.propertyMaintananceCondition.items.maintenanceCondition.choiceValues.map(
              (item, index) => (
                <ConditionDescription key={index} condition={item} />
              ),
            )}
        </DynamicForm>

        <PropertyDescriptionInput
          section={checklist.fixed}
          intl={intl}
          onChangeItem={onChangeItemPropertyDescription()}
        />

        <Box my={4}>
          {/* istanbul ignore next */
          !isValid ? (
            <IconDescription
              color="error"
              label={messages.invalid}
              icon={WarningIcon}
            />
          ) : null}
          <Button fullWidth onClick={validateForm}>
            <FormattedMessage {...messages.saveGeneralConditions} />
          </Button>
        </Box>
      </PageWrapper>
    </Fragment>
  );

  return (
    <Fragment>
      <AppBar
        // eslint-disable-next-line react/jsx-no-bind
        onBack={() => goToChecklist(checkIsBetaTester(authorization))}
        title={messages.title}
      />
      {isFinished ? ContentComponent() : <LoadingPage />}
    </Fragment>
  );
};

GeneralConditions.propTypes = {
  goToChecklist: PropTypes.func,
  fetchChecklist: PropTypes.func,
  fetchChecklistFromStorage: PropTypes.func,
  onChangeItemChecklist: PropTypes.func,
  onChangeItemPropertyDescription: PropTypes.func,
  checklist: PropTypes.shape(),
  intl: PropTypes.object,
  authorization: PropTypes.object,
};

const debounceInputUpdate = debounce((dispatch, updateParams) => {
  dispatch(updateStorageChecklistItem(updateParams));
}, DEBOUNCE_STORAGE_UPDATE_WAIT_TIME);

export const mapStateToProps = createStructuredSelector({
  checklist: selectChecklist,
  authorization: selectAuthorization,
});

export const mapDispatchToProps = (dispatch, ownProps) => ({
  goToChecklist: isBetaTester =>
    dispatch(
      push(
        isBetaTester
          ? ROUTES.NEW_CHECKLIST(ownProps.params.id)
          : ROUTES.CHECKLIST(ownProps.params.id),
      ),
    ),
  fetchChecklist: () => dispatch(fetchChecklistAction()),
  fetchChecklistFromStorage: ({ userId }) =>
    dispatch(
      fetchChecklistFromStorageAction({
        userId,
        photoSessionId: ownProps.params.id,
      }),
    ),
  onChangeItemChecklist: ({ sectionType, userId }) => ({
    section,
    itemSlug,
    value,
    previousValue,
  }) => {
    dispatch(
      checklistItemChanged({
        sectionType,
        section,
        itemSlug,
        value,
        previousValue,
        photoSessionId: ownProps.params.id,
      }),
    );
    dispatch(
      updateStorageChecklistItem({
        sectionType,
        section,
        itemSlug,
        value,
        previousValue,
        photoSessionId: ownProps.params.id,
        userId,
      }),
    );
  },
  onChangeItemPropertyDescription: (
    sectionType = SECTIONS_TYPE.FIXED,
    userId = getUser().user_id,
  ) => ({ section, itemSlug, value, previousValue }) => {
    dispatch(
      checklistItemChanged({
        sectionType,
        section,
        itemSlug,
        value,
        previousValue,
        photoSessionId: ownProps.params.id,
      }),
    );
    debounceInputUpdate(dispatch, {
      sectionType,
      section,
      itemSlug,
      value,
      previousValue,
      photoSessionId: ownProps.params.id,
      userId,
    });
  },
});

export default compose(
  withRouter,
  injectIntl,
  connect(
    mapStateToProps,
    mapDispatchToProps,
  ),
)(GeneralConditions);
